import React, { useEffect, useState } from 'react';
import AmenitiesBookingModal from './AmenitiesBookingModal';
import { useToken, useGuest } from '../App';
import { API_ENDPOINTS } from '../config/api';
import { Card, Badge, Button, Spinner, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useHotelTitle } from '../hooks/useHotelTitle';

const GuestExplore: React.FC = () => {
  const { hotelId, token } = useToken()!;
  const guest = useGuest();
  const navigate = useNavigate();
  const { hotelName } = useHotelTitle('Explore');
  const [amenities, setAmenities] = useState<any[]>([]);
  const [emergencyContacts, setEmergencyContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAmenity, setSelectedAmenity] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [fabExpanded, setFabExpanded] = useState(false);

  useEffect(() => {
    if (!hotelId) return;
    setLoading(true);
    setError('');
    
    // Fetch amenities
    Promise.all([
      fetch(API_ENDPOINTS.AMENITIES(hotelId)),
      fetch(API_ENDPOINTS.EMERGENCY_CONTACTS(hotelId))
    ])
      .then(responses => Promise.all(responses.map(res => res.json())))
      .then(([amenitiesData, contactsData]) => {
        setAmenities(Array.isArray(amenitiesData) ? amenitiesData : []);
        setEmergencyContacts(Array.isArray(contactsData) ? contactsData : []);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load data. Please try again.');
        setLoading(false);
      });
  }, [hotelId]);

  const handleAmenityClick = (amenity: any) => {
    // Navigate to amenities page with the specific amenity ID as a parameter
    navigate(`/${token}/amenities?amenity=${amenity.id}`);
  };

  const handleFabClick = () => {
    setFabExpanded(!fabExpanded);
  };

  const handleFabItemClick = (action: () => void) => {
    setFabExpanded(false);
    action();
  };

  const quickActions = [
    {
      icon: 'fas fa-utensils',
      title: 'Order Food',
      description: 'Browse our menu & order room service',
      color: 'primary',
      action: () => navigate(`/${token}/food`)
    },
    {
      icon: 'fas fa-phone',
      title: 'Call Reception',
      description: 'Get assistance anytime',
      color: 'success',
      action: () => navigate(`/${token}/services`)
    },
    {
      icon: 'fas fa-receipt',
      title: 'My Orders',
      description: 'Track your food orders',
      color: 'info',
      action: () => navigate(`/${token}/orders`)
    },
  ];

  const fabItems = [
    {
      icon: 'fas fa-swimming-pool',
      title: 'Amenities',
      color: '#4a6cf7',
      action: () => navigate(`/${token}/amenities`)
    },
    {
      icon: 'fas fa-location-dot',
      title: 'Nearby',
      color: '#ff6b35',
      action: () => navigate(`/${token}/nearby`)
    }
  ];

  const hotelInfo = [
    {
      icon: 'fas fa-clock',
      title: 'Check-in/Check-out',
      details: 'Check-in: 12:00 PM | Check-out: 11:00 AM'
    },
    {
      icon: 'fas fa-wifi',
      title: 'Wi-Fi',
      details: 'Free Wi-Fi available | Password at reception'
    },
    {
      icon: 'fas fa-parking',
      title: 'Parking',
      details: 'Complimentary parking available'
    },
    {
      icon: 'fas fa-thermometer-half',
      title: 'Room Service',
      details: 'Available 24/7 | Call extension 100'
    }
  ];

  // Static emergency contacts as fallback
  const staticEmergencyContacts = [
    { name: 'Reception', number: '100', icon: 'fas fa-phone' },
    { name: 'Local Police', number: '101', icon: 'fas fa-shield-alt' },
    { name: 'Medical Emergency', number: '102', icon: 'fas fa-ambulance' },
    { name: 'Fire Department', number: '103', icon: 'fas fa-fire-extinguisher' }
  ];

  return (
    <div className="content" style={{ maxWidth: 600, margin: '0 auto', padding: 12, marginTop: 80 }}>
      {/* Header */}
      <div className="mb-4">
        <h2 className="section-title mb-2" style={{ fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 28, color: '#4a6cf7' }}><i className="fas fa-compass"></i></span>
          Explore Your Stay
        </h2>
        <p className="text-muted mb-0">Discover everything our hotel has to offer</p>
      </div>

      {/* Quick Actions */}
      <div className="mb-4">
        <h5 className="mb-3" style={{ fontWeight: 600, color: '#333' }}>
          <i className="fas fa-bolt me-2" style={{ color: '#4a6cf7' }}></i>
          Quick Actions
        </h5>
        <Row className="g-2">
          {quickActions.map((action, index) => (
            <Col xs={4} key={index}>
              <Card 
                className="h-100 border-0 shadow-sm" 
                style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                onClick={action.action}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <Card.Body className="text-center p-3">
                  <div className={`text-${action.color} mb-2`} style={{ fontSize: '1.5rem' }}>
                    <i className={action.icon}></i>
                  </div>
                  <h6 className="fw-bold mb-1" style={{ fontSize: '0.9rem' }}>{action.title}</h6>
                  <small className="text-muted">{action.description}</small>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Hotel Amenities */}
      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading amenities...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : amenities.length > 0 && (
        <div className="mb-4">
          <h5 className="mb-3" style={{ fontWeight: 600, color: '#333' }}>
            <i className="fas fa-swimming-pool me-2" style={{ color: '#4a6cf7' }}></i>
            Hotel Amenities
          </h5>
                    <Row className="g-2">
            {amenities.slice(0, 3).map((amenity) => (
              <Col xs={4} key={amenity.id}>
                <Card 
                  className="h-100 border-0 shadow-sm" 
                  style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                  onClick={() => handleAmenityClick(amenity)}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <Card.Body className="text-center p-3 d-flex flex-column" style={{ height: '100%' }}>
                    <div className="mb-2" style={{ fontSize: '1.5rem', color: '#4a6cf7' }}>
                      <i className="fas fa-swimming-pool"></i>
                    </div>
                    <h6 className="fw-bold mb-1" style={{ fontSize: '0.85rem' }}>{amenity.name}</h6>
                    <div className="mt-auto">
                      <Badge bg={amenity.is_active ? 'success' : 'secondary'} className="small">
                        {amenity.is_active ? 'Available' : 'Unavailable'}
                      </Badge>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* Hotel Information */}
      <div className="mb-4">
        <h5 className="mb-3" style={{ fontWeight: 600, color: '#333' }}>
          <i className="fas fa-info-circle me-2" style={{ color: '#4a6cf7' }}></i>
          Hotel Information
        </h5>
        <Row className="g-2">
          {hotelInfo.map((info, index) => (
            <Col xs={12} sm={6} key={index}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="d-flex align-items-center p-3">
                  <div className="me-3" style={{ fontSize: '1.2rem', color: '#4a6cf7' }}>
                    <i className={info.icon}></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-1" style={{ fontSize: '0.9rem' }}>{info.title}</h6>
                    <small className="text-muted">{info.details}</small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Emergency Contacts */}
      {emergencyContacts.length > 0 && (
      <div className="mb-4">
        <h5 className="mb-3" style={{ fontWeight: 600, color: '#333' }}>
          <i className="fas fa-exclamation-triangle me-2" style={{ color: '#dc3545' }}></i>
          Emergency Contacts
        </h5>
        <Row className="g-1">
          {emergencyContacts.map((contact, index) => (
            <Col xs={6} key={index}>
              <Card className="border-0 shadow-sm" style={{ 
                borderLeft: '3px solid #dc3545',
                minHeight: 'auto'
              }}>
                <Card.Body className="p-2">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="me-2" style={{ color: '#dc3545', fontSize: '14px' }}>
                        <i className={contact.category_icon || contact.icon}></i>
                      </div>
                      <div>
                        <div className="fw-bold" style={{ fontSize: '14px', color: '#dc3545', lineHeight: '1.2' }}>
                          {contact.contact_number || contact.number}
                        </div>
                        <div className="fw-bold" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                          {contact.contact_name || contact.name}
                        </div>
                      </div>
                    </div>
                  </div>
                  {contact.contact_description && (
                    <small className="text-muted d-block mt-1" style={{ fontSize: '10px' }}>
                      {contact.contact_description}
                    </small>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
        </div>
      )}

      {/* Help Section */}
      <div className="mb-4">
        <Card className="border-0" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Card.Body className="text-center p-4">
            <div className="mb-3" style={{ fontSize: '2rem' }}>
              <i className="fas fa-headset"></i>
            </div>
            <h5 className="fw-bold mb-2">Need Help?</h5>
            <p className="mb-3">Our staff is here to make your stay perfect. Don't hesitate to reach out!</p>
            <Button 
              variant="primary" 
              size="lg"
              onClick={() => navigate(`/${token}/services`)}
              style={{
                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                border: 'none',
                borderRadius: '12px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '600',
                boxShadow: '0 4px 15px rgba(40, 167, 69, 0.3)',
                transition: 'all 0.3s ease',
                textTransform: 'none',
                letterSpacing: '0.5px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(40, 167, 69, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(40, 167, 69, 0.3)';
              }}
            >
              <i className="fas fa-phone me-2"></i>
              Contact Reception
            </Button>
          </Card.Body>
        </Card>
      </div>

      {/* Extended Floating Action Button */}
      <div style={{ position: 'fixed', bottom: 90, right: 20, zIndex: 1000 }}>
        {/* FAB Items */}
        {fabExpanded && fabItems.map((item, index) => (
          <div
            key={index}
            style={{
              position: 'absolute',
              bottom: `${(index + 1) * 70}px`,
              right: 0,
              transform: 'scale(0)',
              animation: 'fabItemAppear 0.3s ease forwards',
              animationDelay: `${index * 0.1}s`
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'white',
                borderRadius: '25px',
                padding: '8px 16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                marginBottom: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minWidth: '120px'
              }}
              onClick={() => handleFabItemClick(item.action)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '8px',
                  color: 'white',
                  fontSize: '14px'
                }}
              >
                <i className={item.icon}></i>
              </div>
              <span style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>
                {item.title}
              </span>
            </div>
          </div>
        ))}
        
        {/* Main FAB Button */}
        <button
          onClick={handleFabClick}
          title="Explore more options - Amenities & Nearby places"
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#4a6cf7',
            border: 'none',
            color: 'white',
            fontSize: '20px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(74, 108, 247, 0.4)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: fabExpanded ? 'rotate(45deg)' : 'rotate(0deg)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = fabExpanded ? 'rotate(45deg) scale(1.1)' : 'rotate(0deg) scale(1.1)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(74, 108, 247, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = fabExpanded ? 'rotate(45deg) scale(1)' : 'rotate(0deg) scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(74, 108, 247, 0.4)';
          }}
        >
          <i className="fas fa-plus"></i>
        </button>
      </div>

      {/* CSS Animation */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes fabItemAppear {
            from {
              transform: scale(0);
              opacity: 0;
            }
            to {
              transform: scale(1);
              opacity: 1;
            }
          }
        `
      }} />

      <AmenitiesBookingModal
        show={showModal}
        onHide={() => setShowModal(false)}
        amenity={selectedAmenity}
        hotelId={hotelId || ''}
        guestId={guest?.guest_id || ''}
        onBookingSuccess={() => {}}
      />
    </div>
  );
};

export default GuestExplore; 